const LOAN_FORM_KEY = "loanFormData";
const EARLY_LEAD_KEY = "applyFlowEarlyLeadId";
const PAN_DRAFT_KEY = "applyFlowPanDraft";
const STEP2_RESTORE_KEY = "applyFlowRestoreStep2Contact";

const AADHAAR_NUMBER_KEY = "aadhaarNumber";
const AADHAAR_REF_KEY = "aadhaarReferenceId";
const MOBILE_OTP_KEY = "mobile_otp";

const AADHAAR_KEYS = [
  "aadhaarData",
  AADHAAR_REF_KEY,
  AADHAAR_NUMBER_KEY,
  "aadhaar_mobile",
  "loanFormData",
  "currentLeadId",
  MOBILE_OTP_KEY,
  "mobile_verified",
  "mobileNumber",
  "pan_verified",
  "aadhaar_verified",
];

/** Strip heavy/sensitive KYC blobs before browser storage */
export const sanitizeAadhaarProfile = (profile) => {
  if (!profile || typeof profile !== "object") return profile;

  const {
    photoBase64,
    xmlBase64,
    photo,
    xml,
    ...safeProfile
  } = profile;

  return safeProfile;
};

export const setAadhaarNumberSecure = (aadhaar) => {
  const value = String(aadhaar || "").replace(/\D/g, "");
  sessionStorage.setItem(AADHAAR_NUMBER_KEY, value);
  localStorage.removeItem(AADHAAR_NUMBER_KEY);
};

export const getAadhaarNumberSecure = () =>
  sessionStorage.getItem(AADHAAR_NUMBER_KEY) ||
  localStorage.getItem(AADHAAR_NUMBER_KEY) ||
  "";

export const setAadhaarReferenceSecure = (referenceId) => {
  sessionStorage.setItem(AADHAAR_REF_KEY, referenceId || "");
  localStorage.removeItem(AADHAAR_REF_KEY);
};

export const getAadhaarReferenceSecure = () =>
  sessionStorage.getItem(AADHAAR_REF_KEY) ||
  localStorage.getItem(AADHAAR_REF_KEY) ||
  "";

export const setMobileOtpSecure = (otp) => {
  const value = String(otp || "").trim();
  sessionStorage.setItem(MOBILE_OTP_KEY, value);
  // Keep for submit payload across apply steps (cleared on success).
  localStorage.setItem(MOBILE_OTP_KEY, value);
  try {
    const raw = localStorage.getItem(LOAN_FORM_KEY);
    const loan = raw ? JSON.parse(raw) : {};
    if (loan.otp !== value) {
      localStorage.setItem(
        LOAN_FORM_KEY,
        JSON.stringify({ ...loan, otp: value })
      );
    }
  } catch {
    /* ignore */
  }
};

export const getMobileOtpSecure = () => {
  const fromSession = sessionStorage.getItem(MOBILE_OTP_KEY);
  if (fromSession) return fromSession;
  const fromLocal = localStorage.getItem(MOBILE_OTP_KEY);
  if (fromLocal) return fromLocal;
  try {
    const loan = JSON.parse(localStorage.getItem(LOAN_FORM_KEY) || "{}");
    return String(loan?.otp || "").trim();
  } catch {
    return "";
  }
};

export const clearMobileOtpSecure = () => {
  sessionStorage.removeItem(MOBILE_OTP_KEY);
  localStorage.removeItem(MOBILE_OTP_KEY);
};

/** Step 2 → Step 1: clear Aadhaar and everything after it */
export const clearFromAadhaarStep = () => {
  AADHAAR_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  sessionStorage.removeItem(EARLY_LEAD_KEY);
  sessionStorage.removeItem(PAN_DRAFT_KEY);
  sessionStorage.removeItem(STEP2_RESTORE_KEY);
};

/** After successful submit / thank-you — wipe apply PII from browser */
export const clearApplyFlowOnSuccess = () => {
  clearFromAadhaarStep();
  clearLoanStepDraft();
  // apply-loan resume keys (no-op when unused; keeps /apply-now safe)
  [
    "applyLoanStep",
    "applyLoanAadhaarNumber",
    "applyLoanAadhaarRef",
    "applyLoanActive",
    "applyLoanMobileOtpDraft",
    "applyLoanAadhaarOtpDraft",
  ].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

/** Step 3 → Step 2: restore mobile/email on return from loan form */
export const markStep2ContactRestore = () => {
  sessionStorage.setItem(STEP2_RESTORE_KEY, "1");
};

/** Step 1 → Step 2: fresh contact fields (no restore) */
export const clearStep2ContactRestore = () => {
  sessionStorage.removeItem(STEP2_RESTORE_KEY);
};

export const consumeStep2ContactRestore = () => {
  const shouldRestore = sessionStorage.getItem(STEP2_RESTORE_KEY) === "1";
  sessionStorage.removeItem(STEP2_RESTORE_KEY);
  return shouldRestore;
};

/** Step 3 → Step 2: keep contact data; persist loan step draft for return */
export const saveLoanStepDraft = ({ pan, panStatus, earlyLeadId }) => {
  if (pan) {
    sessionStorage.setItem(
      PAN_DRAFT_KEY,
      JSON.stringify({ pan, panStatus: panStatus || "" })
    );
  }

  if (earlyLeadId) {
    sessionStorage.setItem(EARLY_LEAD_KEY, String(earlyLeadId));
  }
};

export const getLoanStepDraft = () => {
  const panRaw = sessionStorage.getItem(PAN_DRAFT_KEY);
  const earlyLeadId = sessionStorage.getItem(EARLY_LEAD_KEY);

  return {
    panDraft: panRaw ? JSON.parse(panRaw) : null,
    earlyLeadId: earlyLeadId || null,
  };
};

export const hasCompletedStep2 = () => Boolean(localStorage.getItem(LOAN_FORM_KEY));

export const clearLoanStepDraft = () => {
  sessionStorage.removeItem(EARLY_LEAD_KEY);
  sessionStorage.removeItem(PAN_DRAFT_KEY);
};
